/**
 * ChipsRow — inline suggestion chips rendered below an AgentMessage.
 *
 * Visual contract (locked 2026-06-15):
 *   - Soft pill chips, 8px gap, wrap freely
 *   - Indented 44px from the left to align with the agent message body
 *     (32px avatar + 12px gap)
 *   - Each chip enters with a 60ms stagger, scale 0.95 → 1
 *   - Warm chips (`warm_slug` set) carry a tiny "✦" badge AND an
 *     amber-tinted border so the user can read "instant load on click"
 *     without an explicit label
 *
 * Day 7 polish (2026-06-15):
 *   - Touch targets bumped to ≥36px height on mobile
 *   - useReducedMotion gates the entrance animation
 *   - focus-visible ring for keyboard navigation (amber accent)
 *
 * Click handler is passed in by the parent.
 */

"use client";

import { motion, useReducedMotion } from "framer-motion";

import { MOTION, STAGGER, ms } from "@/lib/design-tokens";
import { cn } from "@/lib/utils";

import type { Chip } from "./types";

export interface ChipsRowProps {
  chips: Chip[];
  onChipClick: (chip: Chip) => void;
}

export function ChipsRow({ chips, onChipClick }: ChipsRowProps) {
  const reduce = useReducedMotion();

  if (chips.length === 0) {
    return null;
  }
  return (
    <div
      className="flex flex-wrap gap-2"
      style={{ marginTop: 14, paddingLeft: 44 }}
    >
      {chips.map((chip, i) => {
        const isWarm = Boolean(chip.warm_slug);
        return (
          <motion.button
            key={`${chip.label}-${chip.search_query}-${i}`}
            type="button"
            onClick={() => onChipClick(chip)}
            initial={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.95, y: 4 }}
            animate={reduce ? { opacity: 1 } : { opacity: 1, scale: 1, y: 0 }}
            transition={{
              duration: ms(reduce ? MOTION.fast : MOTION.base),
              delay: reduce ? 0 : ms(STAGGER.chips * i + 120),
              ease: [0.16, 1, 0.3, 1],
            }}
            className={cn(
              "rounded-full text-[13px] transition",
              "hover:-translate-y-0.5 hover:brightness-110",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1",
              "focus-visible:ring-offset-[#0a0908]",
              isWarm ? "agent-chip-warm" : "agent-chip",
            )}
            style={{
              padding: "8px 16px",
              minHeight: 36,
              border: isWarm
                ? "1px solid rgba(244,169,60,0.45)"
                : "1px solid rgba(255,255,255,0.1)",
              background: isWarm
                ? "rgba(244,169,60,0.08)"
                : "rgba(255,255,255,0.04)",
              color: "hsl(var(--foreground))",
              // focus-visible ring colour matches amber accent
              ["--tw-ring-color" as string]: "#f4a93c",
            }}
            aria-label={
              isWarm
                ? `${chip.label} (warm comparison, loads instantly)`
                : chip.label
            }
          >
            {isWarm ? (
              <span
                aria-hidden="true"
                style={{ color: "#f4a93c", fontSize: "var(--fs-micro)", marginRight: 6 }}
              >
                ✦
              </span>
            ) : null}
            {chip.label}
          </motion.button>
        );
      })}
    </div>
  );
}
